import { useCallback, useState } from "react";
import { Warning } from "@phosphor-icons/react";

/**
 * Promise-based confirmation dialog. Replaces `window.confirm` with a
 * styled, accessible modal.
 *
 *   const { confirm, dialog } = useConfirm();
 *   const ok = await confirm({
 *     title: "Eliminar 12 productos",
 *     description: "Esta acción no se puede deshacer.",
 *     confirmLabel: "Eliminar",
 *     tone: "danger", // 'danger' | 'default'
 *   });
 *
 * Render `{dialog}` once in the consuming component's tree.
 */
export function useConfirm() {
  const [state, setState] = useState(null);

  const confirm = useCallback(
    (opts) =>
      new Promise((resolve) => {
        setState({ ...opts, resolve });
      }),
    [],
  );

  const close = (value) => {
    if (state?.resolve) state.resolve(value);
    setState(null);
  };

  const dialog = state ? (
    <div
      className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      data-testid="confirm-dialog"
      onKeyDown={(e) => {
        if (e.key === "Escape") close(false);
        if (e.key === "Enter") close(true);
      }}
    >
      <div
        className="bg-white max-w-md w-full border border-zinc-200 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 px-5 pt-5 pb-4">
          {state.tone !== "default" && (
            <div className="w-10 h-10 bg-[#FFEBEE] text-[#FF2A00] flex items-center justify-center shrink-0">
              <Warning size={20} weight="bold" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3
              id="confirm-dialog-title"
              className="text-base font-bold tracking-tight h-display"
            >
              {state.title || "¿Confirmar acción?"}
            </h3>
            {state.description && (
              <p className="text-sm text-muted2 mt-1.5 leading-relaxed">
                {state.description}
              </p>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 bg-zinc-50 border-t border-zinc-200">
          <button
            type="button"
            onClick={() => close(false)}
            className="px-4 py-1.5 text-sm border border-zinc-300 hover:bg-white"
            data-testid="confirm-cancel"
            autoFocus
          >
            {state.cancelLabel || "Cancelar"}
          </button>
          <button
            type="button"
            onClick={() => close(true)}
            className={`px-4 py-1.5 text-sm font-medium text-white ${
              state.tone === "default"
                ? "bg-klein hover:bg-klein/90"
                : "bg-[#FF2A00] hover:bg-[#E62500]"
            }`}
            data-testid="confirm-ok"
          >
            {state.confirmLabel || "Eliminar"}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return { confirm, dialog };
}
